/**
 * Design System Utilities
 * Helper functions and constants based on design.md specifications
 */

import { colors } from '../theme/theme';

// ============================================================================
// Status Badge Utilities
// ============================================================================

export type PredictionStatus = 'PENDING' | 'IN_PROGRESS' | 'APPROVED' | 'REJECTED' | 'READY';

export interface StatusConfig {
  color: string;
  backgroundColor: string;
  label: string;
}

export const getStatusConfig = (status: PredictionStatus): StatusConfig => {
  switch (status) {
    case 'APPROVED':
      return {
        color: '#FFFFFF',
        backgroundColor: colors.success.main,
        label: 'Approved',
      };
    case 'IN_PROGRESS':
      return {
        color: '#92400E',
        backgroundColor: colors.warning.main,
        label: 'In Progress',
      };
    case 'REJECTED':
      return {
        color: '#FFFFFF',
        backgroundColor: colors.error.main,
        label: 'Rejected',
      };
    case 'PENDING':
      return {
        color: '#FFFFFF',
        backgroundColor: colors.text.disabled,
        label: 'Pending',
      };
    case 'READY':
      return {
        color: '#FFFFFF',
        backgroundColor: colors.info.main,
        label: 'Ready',
      };
    default:
      return {
        color: '#FFFFFF',
        backgroundColor: colors.text.disabled,
        label: status || 'Unknown',
      };
  }
};

// ============================================================================
// Tag Selection Utilities
// ============================================================================

export const getTagStyles = (selected: boolean) => ({
  backgroundColor: selected ? 'rgba(99, 102, 241, 0.08)' : colors.background.elevated,
  color: selected ? '#A5B4FC' : colors.text.secondary,
  borderColor: selected ? 'rgba(99, 102, 241, 0.3)' : colors.divider,
  '&:hover': {
    backgroundColor: selected ? 'rgba(99, 102, 241, 0.12)' : colors.background.paper,
    borderColor: 'rgba(99, 102, 241, 0.5)',
    cursor: 'pointer',
  },
});

// ============================================================================
// Chart Color Palette
// ============================================================================

export const chartColors = {
  primary: [
    '#6366F1',
    colors.chart.secondary,
    colors.chart.tertiary,
    '#818CF8',
    colors.info.main,
    colors.success.main,
    colors.warning.main,
  ],
  background: colors.background.paper,
  gridColor: colors.divider,
  textColor: colors.text.secondary,
};

// ============================================================================
// Animation Constants
// ============================================================================

export const animations = {
  duration: {
    fast: 150,
    normal: 200,
    slow: 300,
  },
  easing: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
};

// ============================================================================
// Spacing Helpers
// ============================================================================

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
  '3xl': 64,
};

// ============================================================================
// Layout Constants
// ============================================================================

export const layout = {
  navHeight: 64,
  maxContentWidth: 1400,
  sidebarWidth: 240,
  detailPanelRatio: {
    left: 30,
    center: 35,
    right: 35,
  },
};

// ============================================================================
// Breakpoint Utilities
// ============================================================================

export const breakpoints = {
  xs: 480,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

// Helper for media queries
export const mediaQuery = (breakpoint: keyof typeof breakpoints) => 
  `@media (min-width: ${breakpoints[breakpoint]}px)`;

// ============================================================================
// Common Style Objects
// ============================================================================

export const commonStyles = {
  card: {
    borderRadius: '8px',
    padding: spacing.lg,
    backgroundColor: colors.background.paper,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
  },
  
  elevatedCard: {
    borderRadius: '12px',
    padding: spacing.xl,
    backgroundColor: colors.background.elevated,
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
  },
  
  dataPanel: {
    borderRadius: '8px',
    padding: spacing.md,
    backgroundColor: colors.background.paper,
    border: `1px solid ${colors.divider}`,
  },
  
  pageContainer: {
    maxWidth: layout.maxContentWidth,
    margin: '0 auto',
    padding: spacing.xl,
  },
  
  sectionSpacing: {
    marginBottom: spacing.xl,
  },
};

// ============================================================================
// Avatar/Initial Generator
// ============================================================================

export const getInitials = (name: string): string => {
  if (!name) return '?';
  
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const getAvatarColor = (name: string): string => {
  // Generate a consistent color based on the name
  const colors = [
    '#3B82F6', '#8B5CF6', '#06B6D4', '#10B981', '#F59E0B',
    '#EF4444', '#EC4899', '#6366F1', '#14B8A6', '#F97316',
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

// ============================================================================
// Format Utilities
// ============================================================================

export const formatDate = (date: string | Date): string => {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }
};

export const formatDateTime = (date: string | Date): string => {
  if (!date) return '';
  
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export default {
  getStatusConfig,
  getTagStyles,
  chartColors,
  animations,
  spacing,
  layout,
  breakpoints,
  mediaQuery,
  commonStyles,
  getInitials,
  getAvatarColor,
  formatDate,
  formatDateTime,
};
